/**
 * @name StyledTextField
 * @author Keven Adan Be          '& .MuiInputLabel-root': {
               fontSize: fontSize,
               transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
               // Posição inicial do label (centralizado no campo)
               transform: `translate(14px, ${height/2 - 8}px) scale(1)`,
               // Posição quando focado ou preenchido (bem acima do campo)
               '&.Mui-focused, &.MuiFormLabel-filled': {
                    transform: 'translate(14px, -16px) scale(0.75)',
                    zIndex: 10,
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.background.default,
                    padding: '0 6px',
               },
               // Ajuste específico para o estado shrink
               '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -16px) scale(0.75)',
                    zIndex: 10,
                    backgroundColor: theme.palette.background.default,
                    padding: '0 6px',
               },
          },b
 * @description Componente TextField padronizado com estilos glassmorphism
 * Implementa o requisito TC11 para padronização de campos de entrada
 * @param {StyledTextFieldProps} props - Props do componente
 * @returns {JSX.Element} TextField com estilo padronizado
 * @package src/components/common/fields/StyledTextField.tsx
 */

import useThemeContext from "@/themes/hooks/useThemeContext";
import { getGlassInput } from "@/themes/styles/glassStyles";
import { Box, TextField, TextFieldProps } from "@mui/material";
import React from "react";

interface StyledTextFieldProps extends Omit<TextFieldProps, "sx"> {
  /**
   * Altura personalizada do campo (padrão: 56px)
   */
  height?: number;

  /**
   * Tamanho da fonte (padrão: 1.1rem)
   */
  fontSize?: string;

  /**
   * Aplicar efeito glass container
   */
  withGlassContainer?: boolean;

  /**
   * Margem inferior (padrão: 0)
   */
  marginBottom?: number;

  /**
   * Estilos customizados adicionais
   */
  customSx?: object;
}

const StyledTextField: React.FC<StyledTextFieldProps> = ({
  height = 56,
  fontSize = "1.1rem",
  withGlassContainer = true,
  marginBottom = 0,
  customSx = {},
  ...textFieldProps
}) => {
  const { theme } = useThemeContext();

  const baseTextFieldStyles = {
    // Container principal - altura com espaço superior para label
    height: height,
    paddingTop: "8px",

    "& .MuiInputBase-root": {
      fontSize: fontSize,
      height: height,
      backgroundColor: "transparent",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    "& .MuiInputLabel-root": {
      fontSize: fontSize,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      // Posição inicial do label (centralizado no campo)
      transform: `translate(14px, ${height / 2 - 8}px) scale(1)`,
      // Posição quando focado ou preenchido (bem acima do campo)
      "&.Mui-focused, &.MuiFormLabel-filled": {
        transform: "translate(14px, -16px) scale(0.75)",
        zIndex: 10,
        color: theme.palette.primary.main,
      },
      // Ajuste específico para o estado shrink
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -16px) scale(0.75)",
        zIndex: 10,
      },
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "transparent",
      height: height,
      // Espaçamento interno adequado e consistente
      "& .MuiOutlinedInput-input": {
        padding: "14px",
        height: "auto",
      },
      "& fieldset": {
        borderColor: `rgba(255, 255, 255, ${theme.palette.mode === "dark" ? 0.2 : 0.3})`,
        borderWidth: "1px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      },
      "&:hover fieldset": {
        borderColor: `rgba(255, 255, 255, ${theme.palette.mode === "dark" ? 0.3 : 0.4})`,
        borderWidth: "1px",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: "2px",
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
      },
      "&.Mui-error fieldset": {
        borderColor: theme.palette.error.main,
        borderWidth: "2px",
        boxShadow: `0 0 0 2px ${theme.palette.error.main}20`,
      },
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.875rem",
      marginTop: "4px",
      marginLeft: "14px",
    },
    ...customSx,
  };

  if (withGlassContainer) {
    return (
      <Box sx={{ ...getGlassInput(theme), mb: marginBottom }}>
        <TextField {...textFieldProps} sx={baseTextFieldStyles} />
      </Box>
    );
  }

  return (
    <TextField
      {...textFieldProps}
      sx={{
        ...baseTextFieldStyles,
        mb: marginBottom,
      }}
    />
  );
};

export default StyledTextField;
